import { placeTypeMap } from "@mnk/shared/models/placeType";
import type { PlaceType } from "@mnk/shared/models/placeType";
import type { RoadType } from "@mnk/shared/models/roadType";
import { useCallback, useMemo } from "react";

interface Position {
  x: number;
  y: number;
}

interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface HataageText {
  lines: LineItem[];
}

export interface LineItem {
  content: string;
  config: {
    textColor: string;
    backgroundColor: string;
  };
  modifiedContent?: string;
  modifiedConfig?: {
    textColor: string;
    backgroundColor: string;
  };
  noteContent?: string;
  noteConfig?: {
    textColor: string;
    backgroundColor: string;
  };
}

interface HataageConfig {
  lineWidth: number;
  hookLength: number;
  fontSize: number;
  lineHeight: number;
  padding: number;
  fontFamily: string;
  lineColor: string;
}

export interface KiloPost {
  startKiloPost: number;
  endKiloPost: number;
  config: {
    textColor: string;
    backgroundColor: string;
  };
}

interface HataageProps {
  ctx: CanvasRenderingContext2D;
  text: HataageText;
  position: Position;
  kiloPost: KiloPost;
  modifiedKiloPost?: KiloPost;
  placeType: PlaceType;
  roadType: RoadType;
  dates: string[];
  config?: {
    lineWidth?: number;
    lineColor?: string;
    textColor?: string;
    backgroundColor?: string;
  };
}

// 旗揚げ（横線とテキスト）の描画とインタラクションを管理するフック
export const useHataage = (customConfig?: Partial<HataageConfig>) => {
  // 旗揚げの設定
  const config = useMemo<HataageConfig>(
    () => ({
      lineWidth: 200,
      hookLength: 10,
      fontSize: 20,
      lineHeight: 25,
      padding: 2,
      fontFamily: "sans-serif",
      lineColor: "#000000",
      textColor: "#000000",
      backgroundColor: "#FFFFFF",
      ...customConfig,
    }),
    [customConfig],
  );

  // テキストの全高さを計算
  const calculateTextHeight = (text: HataageText): number => {
    return text.lines.length * config.lineHeight + config.padding * 2;
  };

  // 旗揚げの境界を計算
  const getHataageBounds = (position: Position, text: HataageText): Bounds => {
    const textHeight = calculateTextHeight(text);

    return {
      x: position.x,
      y: position.y - textHeight,
      width: config.lineWidth,
      height: textHeight + config.hookLength,
    };
  };

  // 旗揚げを描画する関数
  const drawHataage = useCallback(
    (props: HataageProps): Promise<void> => {
      return new Promise((resolve) => {
        const {
          ctx,
          text,
          position: itemPosition,
          config: customConfig,
          kiloPost,
          modifiedKiloPost,
          placeType,
          dates,
          roadType,
        } = props;
        const { lineWidth, fontSize, lineHeight, padding, fontFamily, lineColor } = {
          ...config,
          ...customConfig,
        };

        // コンテキストの状態を保存
        ctx.save();
        ctx.translate(itemPosition.x, itemPosition.y);

        // lineWidthが負の場合（線が右から左）、テキストを線の範囲内に配置
        const textBaseX = lineWidth < 0 ? lineWidth + 10 : 10;

        // テキスト位置の決定
        // 文字は必ず見える位置に配置する（セーフマージン50px）
        const safeMargin = 50;
        let textX = 0;
        if (itemPosition.x + textBaseX < safeMargin) {
          // テキストの実際の位置がセーフマージンより左の場合、調整する
          textX = Math.max(0, -(itemPosition.x + textBaseX) + safeMargin);
        }

        // 線のスタイル設定
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 1;

        // 横線を描画（ホッチキスの針のような形）
        // メイン横線
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(lineWidth, 0);
        ctx.stroke();

        const hookY = roadType === "Upbound" ? -10 : 0;
        const hookLength = roadType === "Upbound" ? 0 : 10;

        // 左側の鉤
        ctx.beginPath();
        ctx.moveTo(0, hookY);
        ctx.lineTo(0, hookLength);
        ctx.stroke();

        // 右側の鉤
        ctx.beginPath();
        ctx.moveTo(lineWidth, hookY);
        ctx.lineTo(lineWidth, hookLength);
        ctx.stroke();

        // キロポストを描画
        ctx.beginPath();
        ctx.lineTo(lineWidth, 0);
        ctx.stroke();
        ctx.font = `${fontSize}px ${fontFamily}`;
        // 旗揚げは flag 単位で上り/下りが決まる（Both は発生しない）。KPに向き接頭辞を付ける
        const roadTypeLabel = roadType === "Upbound" ? "上り" : roadType === "Downbound" ? "下り" : "";
        const kiloPostText = `${roadTypeLabel}KP: ${kiloPost.startKiloPost} - ${kiloPost.endKiloPost}`;
        const placeText = placeType !== "Unknown" ? `(${placeTypeMap[placeType]})` : "";
        const dateText = dates.map((date, i) => `施工日${i + 1}: ${date}`).join(" ");

        const yOffset = fontSize + 8;
        const paddingText = 5; // パディングを追加

        // キロポストと場所のテキストを描画
        const firstPartText = `${kiloPostText} ${placeText}`;
        const firstPartMetrics = ctx.measureText(firstPartText);
        const firstPartHeight = firstPartMetrics.actualBoundingBoxAscent + firstPartMetrics.actualBoundingBoxDescent;

        // キロポストと場所のテキストの背景を描画
        ctx.fillStyle = kiloPost.config.backgroundColor;
        ctx.fillRect(
          textX + textBaseX - padding,
          yOffset - firstPartMetrics.actualBoundingBoxAscent - padding,
          firstPartMetrics.width + padding * 2,
          firstPartHeight + padding * 2,
        );

        // キロポストと場所のテキストを描画
        ctx.fillStyle = kiloPost.config.textColor;
        ctx.fillText(firstPartText, textX + textBaseX, yOffset);

        // 日付テキストの背景を白に設定
        const dateMetrics = ctx.measureText(dateText);
        const dateHeight = dateMetrics.actualBoundingBoxAscent + dateMetrics.actualBoundingBoxDescent;

        // 日付テキストの背景を白で描画
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(
          textX + textBaseX + firstPartMetrics.width + 5 - padding,
          yOffset - dateMetrics.actualBoundingBoxAscent - padding,
          dateMetrics.width + padding * 2,
          dateHeight + padding * 2,
        );

        // 日付テキストを描画
        ctx.fillStyle = kiloPost.config.textColor;
        ctx.fillText(dateText, textX + textBaseX + firstPartMetrics.width + 5, yOffset);

        if (modifiedKiloPost) {
          const modifiedKiloPostText = `${roadTypeLabel}KP: ${modifiedKiloPost.startKiloPost} - ${modifiedKiloPost.endKiloPost} ${placeText}`;
          const modifiedTextMetrics = ctx.measureText(modifiedKiloPostText);
          const modifiedTextHeight =
            modifiedTextMetrics.actualBoundingBoxAscent + modifiedTextMetrics.actualBoundingBoxDescent;
          ctx.fillStyle = modifiedKiloPost.config.backgroundColor;
          ctx.fillRect(
            textX + textBaseX - paddingText,
            yOffset + dateHeight + 5 - paddingText,
            modifiedTextMetrics.width + paddingText * 2,
            modifiedTextHeight + paddingText * 2,
          );
          ctx.fillStyle = modifiedKiloPost.config.textColor;
          ctx.fillText(
            modifiedKiloPostText,
            textX + textBaseX,
            yOffset + dateHeight + 5 + modifiedTextHeight - modifiedTextMetrics.actualBoundingBoxDescent,
          );
        }

        // テキストを鍵の上に描画（背景を白にする）
        ctx.font = `${fontSize}px ${fontFamily}`;

        text.lines.forEach((line, index) => {
          const textY = -(padding + (index + 1) * lineHeight);
          const textWidth = ctx.measureText(line.content).width;
          const textHeight = fontSize;

          // テキストの背景を描画
          ctx.fillStyle = line.config.backgroundColor;
          ctx.fillRect(
            textX + textBaseX - padding * 2,
            textY - textHeight - padding,
            textWidth + padding * 4,
            textHeight + padding * 2,
          );

          // テキスト自体を描画
          ctx.fillStyle = line.config.textColor;
          ctx.fillText(line.content, textX + textBaseX, textY);

          let rightEdgeOffset = textWidth + padding * 2;

          if (line.modifiedContent && line.modifiedConfig) {
            const modifiedTextWidth = ctx.measureText(line.modifiedContent).width;
            const modifiedOffsetX = textWidth + padding * 6;

            ctx.fillStyle = line.modifiedConfig.backgroundColor;
            ctx.fillRect(
              textX + textBaseX + modifiedOffsetX,
              textY - textHeight - padding,
              modifiedTextWidth + padding * 4,
              textHeight + padding * 2,
            );
            ctx.fillStyle = line.modifiedConfig.textColor;
            ctx.fillText(line.modifiedContent, textX + textBaseX + modifiedOffsetX, textY);

            rightEdgeOffset = modifiedOffsetX + modifiedTextWidth + padding * 2;
          }

          if (line.noteContent && line.noteConfig) {
            const noteTextWidth = ctx.measureText(line.noteContent).width;
            const noteOffsetX = rightEdgeOffset + padding * 4;

            ctx.fillStyle = line.noteConfig.backgroundColor;
            ctx.fillRect(
              textX + textBaseX + noteOffsetX,
              textY - textHeight - padding,
              noteTextWidth + padding * 4,
              textHeight + padding * 2,
            );
            ctx.fillStyle = line.noteConfig.textColor;
            ctx.fillText(line.noteContent, textX + textBaseX + noteOffsetX, textY);
          }
        });

        // コンテキストの状態を復元
        ctx.restore();

        // 描画が完了したことを通知
        resolve();
      });
    },
    [config],
  );

  // 旗揚げ内の点かどうかをチェックする関数
  const isPointInHataage = (point: Position, text: HataageText, position: Position, lineWidth?: number): boolean => {
    const textHeight = calculateTextHeight(text);

    // 各行のテキスト幅を概算
    let maxTextWidth = 0;
    for (const line of text.lines) {
      const baseWidth = line.content.length * config.fontSize * 0.6;
      const modifiedWidth = line.modifiedContent ? line.modifiedContent.length * config.fontSize * 0.6 : 0;
      const lineWidth = baseWidth + modifiedWidth + config.padding * 4;
      maxTextWidth = Math.max(maxTextWidth, lineWidth);
    }

    // キロポストとプレイスタイプの幅も考慮
    const bottomTextWidth = 300;
    maxTextWidth = Math.max(maxTextWidth, bottomTextWidth);

    // 全体の高さ（テキスト＋キロポスト）
    const totalHeight = textHeight + config.hookLength + 60;

    const safeMargin = 50;
    if (position.x < safeMargin) {
      // テキストが調整されている場合
      const actualTextX = -position.x + safeMargin;

      // 描画されている全ての要素を含むクリック可能領域
      // 1. 線の見える部分（もしあれば）
      // 2. 調整されたテキスト位置
      const clickableLeft = Math.max(0, Math.min(position.x, actualTextX));
      const clickableRight = Math.max(
        position.x + config.lineWidth > 0 ? position.x + config.lineWidth : 0,
        actualTextX + maxTextWidth,
      );

      return (
        point.x >= clickableLeft &&
        point.x <= clickableRight &&
        point.y >= position.y - textHeight &&
        point.y <= position.y + totalHeight - textHeight
      );
    }

    // 通常の場合（position.x >= safeMargin）
    const effectiveLineWidth = lineWidth ?? config.lineWidth;
    const leftX = Math.max(0, Math.min(position.x, position.x + effectiveLineWidth));
    const bounds = getHataageBounds(position, text);

    return (
      point.x >= leftX &&
      point.x <= leftX + config.lineWidth &&
      point.y >= bounds.y &&
      point.y <= bounds.y + bounds.height
    );
  };

  return {
    drawHataage,
    getHataageBounds,
    isPointInHataage,
    hataageConfig: config,
  };
};
